/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * BIBLIOTEKA SKÓREK — rewizje systemu głównego widziane jako SKÓRKI DO WYBORU (OWNER 11.10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ JEDEN MECHANIZM, NOWY WIDOK ──────────────────────────────────────────────────────────────────┐
 * │ Źródłem jest lista rewizji `SeasonalThemes` — bez własnego stanu i bez drugiego harmonogramu. │
 * │ Skórka w bibliotece = wszystkie rewizje jednego `themeId`. Planowanie idzie istniejącymi       │
 * │ operacjami: szkic → „Zaplanuj"/„Aktywuj"; skórka już użyta → „zaplanuj ponownie" (NOWA rewizja │
 * │ z tą samą treścią) — historyczna rewizja i jej okno zostają nietknięte.                        │
 * └────────────────────────────────────────────────────────────────────────────────────────────────┘
 */
import { ETYKIETA_ZAKRESU, type KolizjaZastosowania, type Skorka, type ZastosowanieSkorki } from '@/lib/api/tresci';
import { ApiError } from '@/lib/api/errors';
import { STREFA } from './czasWarszawa';

export interface PozycjaBiblioteki {
  themeId: string;
  nazwa: string;
  miniatura: string | null;
  minSilnik: number;
  /** Rewizja, z której liczy się podgląd i ponowne użycie: najnowsza bez błędu walidacji. */
  wzor: Skorka;
  /** Skórki 2.0: rewizja, którą dostaje nowe zastosowanie — najnowsza bez błędu i NIEwyłączona (`null` = brak). */
  doZastosowania: Skorka | null;
  /** Rewizja aktywna teraz (jedna aktywna skórka w systemie). */
  aktywna: Skorka | null;
  /** Przyszłe okna tej skórki, od najbliższego. */
  zaplanowane: Skorka[];
  /** Szkic nowszy niż każda opublikowana rewizja — „Zaplanuj" użyje właśnie jego. */
  szkic: Skorka | null;
  /** Ostatnie zakończone albo wyłączone użycie (z oknem). */
  ostatnieUzycie: Skorka | null;
  rewizje: Skorka[];
}

export interface Biblioteka {
  pozycje: PozycjaBiblioteki[];
  /** Skórki, które mają WYŁĄCZNIE rewizje z błędem walidacji — nie są gotowe do publikacji. */
  odrzucone: Skorka[];
}

const czas = (s: string | null | undefined): number => (s ? Date.parse(s) : Number.NaN);

export function zbudujBiblioteke(rewizje: readonly Skorka[]): Biblioteka {
  const grupy = new Map<string, Skorka[]>();
  for (const r of rewizje) {
    const g = grupy.get(r.themeId) ?? [];
    g.push(r);
    grupy.set(r.themeId, g);
  }

  const pozycje: PozycjaBiblioteki[] = [];
  const odrzucone: Skorka[] = [];
  for (const [themeId, lista] of grupy) {
    const odNajnowszej = [...lista].sort((a, b) => b.revision - a.revision);
    const poprawne = odNajnowszej.filter((r) => r.effectiveStatus !== 'ERROR');
    const wzor = poprawne[0];
    if (!wzor) {
      odrzucone.push(odNajnowszej[0]!);
      continue;
    }
    const opublikowanaNajnowsza = poprawne.find((r) => r.effectiveStatus !== 'DRAFT');
    const szkic = poprawne.find((r) => r.effectiveStatus === 'DRAFT'
      && (!opublikowanaNajnowsza || r.revision > opublikowanaNajnowsza.revision)) ?? null;
    const zaplanowane = poprawne.filter((r) => r.effectiveStatus === 'SCHEDULED')
      .sort((a, b) => (czas(a.startsAt) || 0) - (czas(b.startsAt) || 0));
    const ostatnieUzycie = poprawne
      .filter((r) => (r.effectiveStatus === 'ENDED' || r.effectiveStatus === 'DISABLED') && r.startsAt)
      .sort((a, b) => czas(b.startsAt) - czas(a.startsAt))[0] ?? null;
    pozycje.push({
      themeId,
      nazwa: wzor.name,
      miniatura: poprawne.map((r) => r.thumbnail ?? null).find((u) => !!u) ?? null,
      minSilnik: wzor.minEngineVersion ?? 1,
      wzor,
      doZastosowania: poprawne.find((r) => r.effectiveStatus !== 'DISABLED') ?? null,
      aktywna: poprawne.find((r) => r.effectiveStatus === 'ACTIVE') ?? null,
      zaplanowane,
      szkic,
      ostatnieUzycie,
      rewizje: odNajnowszej,
    });
  }

  // Najpierw to, co dzieje się teraz, potem najbliższe plany, reszta alfabetycznie.
  const klucz = (p: PozycjaBiblioteki): number =>
    p.aktywna ? 0 : p.zaplanowane.length ? 1 + (czas(p.zaplanowane[0]!.startsAt) || 0) / 1e15 : 3;
  pozycje.sort((a, b) => klucz(a) - klucz(b) || a.nazwa.localeCompare(b.nazwa, 'pl'));
  return { pozycje, odrzucone };
}

// ── SKÓRKI 2.0 (OWNER 16.10): użycie skórki = jej ZASTOSOWANIA (gdzie i kiedy), nie rewizje ────────────────

export interface UzycieSkorki {
  /** Zastosowania aktywne w tej chwili (różne miasta / szkoły / osoby mogą mieć tę skórkę jednocześnie). */
  aktywnych: number;
  /** Zastosowania zaplanowane (jeszcze nie aktywne). */
  zaplanowanych: number;
  /** Najbliższe zaplanowane zastosowanie (od najbliższego startu). */
  najblizsze: ZastosowanieSkorki | null;
  /** Ostatnie zakończone albo wyłączone zastosowanie. */
  ostatnie: ZastosowanieSkorki | null;
}

export function uzycieSkorki(themeId: string, zastosowania: readonly ZastosowanieSkorki[]): UzycieSkorki {
  const moje = zastosowania.filter((z) => z.themeId === themeId);
  return {
    aktywnych: moje.filter((z) => z.effectiveStatus === 'ACTIVE').length,
    zaplanowanych: moje.filter((z) => z.effectiveStatus === 'SCHEDULED').length,
    najblizsze: moje.filter((z) => z.effectiveStatus === 'SCHEDULED')
      .sort((a, b) => (czas(a.startsAt) || 0) - (czas(b.startsAt) || 0))[0] ?? null,
    ostatnie: moje.filter((z) => z.effectiveStatus === 'ENDED' || z.effectiveStatus === 'DISABLED')
      .sort((a, b) => (czas(b.startsAt) || 0) - (czas(a.startsAt) || 0))[0] ?? null,
  };
}

/** „Aktywne teraz" i „Nadchodzące" — zastosowania wszystkich skórek, nadchodzące od najbliższego startu. */
export function aktywneINadchodzaceZastosowania(zastosowania: readonly ZastosowanieSkorki[]): {
  aktywne: ZastosowanieSkorki[]; nadchodzace: ZastosowanieSkorki[];
} {
  const wgStartu = (a: ZastosowanieSkorki, b: ZastosowanieSkorki) => (czas(a.startsAt) || 0) - (czas(b.startsAt) || 0);
  return {
    aktywne: zastosowania.filter((z) => z.effectiveStatus === 'ACTIVE').sort(wgStartu),
    nadchodzace: zastosowania.filter((z) => z.effectiveStatus === 'SCHEDULED').sort(wgStartu),
  };
}

/** Gdzie działa: „Wszyscy" albo nazwy adresatów („Warszawa, Wrocław", „XIV LO — Warszawa +2"). */
export function opisAdresatow(z: { scope: ZastosowanieSkorki['scope']; targets: readonly { name: string }[] }, maks = 3): string {
  if (z.scope === 'GLOBAL') return ETYKIETA_ZAKRESU.GLOBAL;
  const nazwy = (z.targets ?? []).map((t) => t.name);
  if (nazwy.length === 0) return ETYKIETA_ZAKRESU[z.scope];
  return nazwy.length <= maks ? nazwy.join(', ') : `${nazwy.slice(0, maks).join(', ')} +${nazwy.length - maks}`;
}

/** Konflikt 409 zapisu zastosowania (szczegóły serwera CC) — lista do pokazania; inne błędy → `null`. */
export function konfliktyZBledu(e: unknown): KolizjaZastosowania[] | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const k = (e.body as { szczegoly?: { konflikty?: unknown } } | undefined)?.szczegoly?.konflikty;
  return Array.isArray(k) && k.length ? (k as KolizjaZastosowania[]) : null;
}

/** Jedno zdanie o kolizji: „Ninja · Warszawa · 30.10.2026 00:00 – 01.11.2026 23:59 · priorytet 10". */
export function opisKolizjiZastosowania(k: KolizjaZastosowania): string {
  const gdzie = k.legacy ? 'Wszyscy (publikacja V1)' : opisAdresatow(k, 4);
  return `${k.name} · ${gdzie} · ${oknoWarszawa(k.startsAt, k.endsAt)} · priorytet ${k.priority}`;
}

/** „Aktywna teraz" i „Najbliższe zaplanowane" — jednym spojrzeniem, ze wszystkich skórek. */
export function aktywneINadchodzace(rewizje: readonly Skorka[]): { aktywna: Skorka | null; nadchodzace: Skorka[] } {
  return {
    aktywna: rewizje.find((r) => r.effectiveStatus === 'ACTIVE') ?? null,
    nadchodzace: rewizje.filter((r) => r.effectiveStatus === 'SCHEDULED')
      .sort((a, b) => (czas(a.startsAt) || 0) - (czas(b.startsAt) || 0)),
  };
}

export type OperacjaPlanu =
  | { rodzaj: 'zaplanuj'; id: number }
  | { rodzaj: 'ponownie'; id: number };

/** „Zaplanuj" z kafelka: świeży szkic planuje się sam; skórka już użyta wraca jako NOWA rewizja. */
export function jakZaplanowac(p: PozycjaBiblioteki): OperacjaPlanu {
  return p.szkic ? { rodzaj: 'zaplanuj', id: p.szkic.id } : { rodzaj: 'ponownie', id: p.wzor.id };
}

export type OperacjaAktywacji =
  | { rodzaj: 'aktywuj'; id: number }
  | { rodzaj: 'ponownie'; id: number };

/**
 * „Aktywuj teraz" z kafelka. Skórka już użyta → ponowne użycie z oknem od TERAZ (ta sama blokada nakładania:
 * aktywna inna skórka = 409 z nazwą i terminem, nic nie jest podmieniane w ciemno).
 */
export function jakAktywowac(p: PozycjaBiblioteki): OperacjaAktywacji {
  return p.szkic ? { rodzaj: 'aktywuj', id: p.szkic.id } : { rodzaj: 'ponownie', id: p.wzor.id };
}

export interface Zgodnosc {
  etykieta: string;
  /** Zdanie dla operatora, gdy starsze aplikacje pokażą zwykły wygląd; `null` = każda wersja 4.0.x. */
  ostrzezenie: string | null;
}

/** Silnik ≥ 2 rysują aplikacje 4.0.10+ — jawnie, nie w szczegółach technicznych. */
export function zgodnosc(minSilnik: number): Zgodnosc {
  return minSilnik >= 2
    ? { etykieta: 'Wymaga aplikacji 4.0.10+', ostrzezenie: 'Starsze wersje aplikacji pokażą standardowy wygląd.' }
    : { etykieta: 'Każda wersja aplikacji 4.0.x', ostrzezenie: null };
}

const FORMAT_DATA = new Intl.DateTimeFormat('pl-PL', { timeZone: STREFA, day: '2-digit', month: '2-digit', year: 'numeric' });
const FORMAT_DATA_KROTKO = new Intl.DateTimeFormat('pl-PL', { timeZone: STREFA, day: '2-digit', month: '2-digit' });
const FORMAT_GODZINA = new Intl.DateTimeFormat('pl-PL', { timeZone: STREFA, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/** „28.10.2026 00:00" w czasie warszawskim. */
export function chwilaWarszawa(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : `${FORMAT_DATA.format(d)} ${FORMAT_GODZINA.format(d)}`;
}

/** Okno w czasie warszawskim: pełne („28.10.2026 00:00 – 02.11.2026 23:59") albo krótkie („28.10–02.11"). */
export function oknoWarszawa(startsAt: string | null | undefined, endsAt: string | null | undefined, krotko = false): string {
  if (krotko) {
    const f = (s: string | null | undefined) => (s ? FORMAT_DATA_KROTKO.format(new Date(s)) : '…');
    return `${f(startsAt)}–${endsAt ? f(endsAt) : 'bez końca'}`;
  }
  return `${startsAt ? chwilaWarszawa(startsAt) : 'od teraz'} – ${endsAt ? chwilaWarszawa(endsAt) : 'bez daty końca'}`;
}

/** Kolizja 409 z systemu głównego jako zdania dla operatora: nazwa i termin każdej nachodzącej skórki. */
export function opisKolizji(e: unknown): string[] | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const kolizje = (e.body as { szczegoly?: { kolizje?: unknown } } | undefined)?.szczegoly?.kolizje;
  if (!Array.isArray(kolizje) || kolizje.length === 0) return null;
  return kolizje.map((k) => {
    const s = (k ?? {}) as { name?: unknown; startsAt?: unknown; endsAt?: unknown };
    const nazwa = typeof s.name === 'string' && s.name ? s.name : 'inna skórka';
    return `${nazwa}: ${oknoWarszawa(typeof s.startsAt === 'string' ? s.startsAt : null, typeof s.endsAt === 'string' ? s.endsAt : null)}`;
  });
}
